/**
 * K8s clusters: list/detail/create/update/delete + service-status + snapshot
 * schedule + snapshots. Cluster-level types live here.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApi, unwrap, k8sPath, arr, k8sKeys } from './_internal';

/** One structured deployment-progress entry the backend records during provisioning
 *  (carried inside cluster metadata, not a separate logs endpoint). */
export interface K8sDeploymentLog {
  timestamp?: string;
  stage?: string;
  level?: string;
  message?: string;
}

export interface K8sClusterMetadata {
  uid?: string;
  name?: string;
  description?: string;
  version?: string;
  runtime?: string;
  endpoint?: { fqdn?: string };
  network?: { cni?: string; pod_cidr?: string; service_cidr?: string; subnet?: string };
  pools?: { name?: string; roles?: string; flavor?: string; min?: number; max?: number; machines?: unknown[] }[];
  addons?: Record<string, { enabled?: boolean; [k: string]: unknown }>;
  /** Real-time provisioning activity log + last failure, surfaced while the cluster comes up. */
  deployment_logs?: K8sDeploymentLog[];
  /** Backend may send a plain string OR a structured log entry ({stage,message,timestamp}). */
  last_error?: string | K8sDeploymentLog;
}
export interface K8sSnapshotSummary {
  last_snapshot_at?: string | null;
  last_snapshot_status?: string | null;
  total_snapshots?: number;
  total_size_bytes?: number;
  schedule_enabled?: boolean;
  next_scheduled_at?: string | null;
}
export interface K8sCluster {
  id: string;
  uid?: string;
  name?: string;
  description?: string;
  status?: string;
  state?: string;
  zone?: string;
  created_at?: string;
  updated_at?: string;
  metadata?: K8sClusterMetadata;
  snapshot_summary?: K8sSnapshotSummary;
  /** Live version reported by the running control plane (preferred over the configured metadata.version). */
  version?: { gitVersion?: string; platform?: string };
}

// In-flight cluster states (create/update/scale/drain/delete in progress). Excludes
// terminal states (active/running/ready, terminated/deleted, error/failed) so a
// settled list stops polling. `terminating`/`deleting` won't match `terminated`/`deleted`.
const CLUSTER_TRANSITIONING = /provision|pending|deploy|creating|updating|resiz|draining|terminating|deleting|draft/i;

export function useClusters(serviceId: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.clusters(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => arr<K8sCluster>(unwrap(await serviceApi.get(k8sPath(serviceId!, 'clusters'))), 'clusters'),
    // Poll while any cluster is still settling so the list reflects status/node
    // changes live; stop once all are steady. React Query clears the interval on unmount.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((c) => CLUSTER_TRANSITIONING.test(c.status ?? c.state ?? '')) ? 10_000 : false,
  });
}

export function useCluster(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.cluster(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 20_000,
    queryFn: async () => {
      const body = unwrap<{ cluster?: K8sCluster } | K8sCluster>(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}`)));
      return (body && typeof body === 'object' && 'cluster' in body ? body.cluster : body) as K8sCluster;
    },
  });
}

/** Deployment-progress logs for the provisioning view — reads `metadata.deployment_logs`
 *  off the cluster object and polls every 5s (matching @console). Gate it via
 *  `enabled` so it only runs while the cluster is still coming up. */
export function useClusterDeploymentLogs(serviceId: string | undefined, id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...k8sKeys.cluster(serviceId ?? '', id ?? ''), 'deployment_logs'] as const,
    enabled: !!serviceId && !!id && enabled,
    retry: false,
    refetchInterval: 5000,
    queryFn: async () => {
      const body = unwrap<{ cluster?: K8sCluster } | K8sCluster>(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}`)));
      const cluster = (body && typeof body === 'object' && 'cluster' in body ? body.cluster : body) as K8sCluster;
      return {
        logs: cluster?.metadata?.deployment_logs ?? [],
        last_error: cluster?.metadata?.last_error,
        status: cluster?.status ?? cluster?.state ?? '',
      };
    },
  });
}

export function useCreateCluster(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(k8sPath(serviceId, 'clusters'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.clusters(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteCluster(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(k8sPath(serviceId, `clusters/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.clusters(serviceId) }); options?.onSuccess?.(...a); },
  });
}

// ---- Service status (systemd per node) -----------------------------------

/** Real fields from service_status response (ServiceStatusEntry in legacy). */
export interface ServiceStatusEntry {
  /** Real field: `service` (systemd unit name). `name` kept as fallback. */
  service?: string;
  name?: string;
  /** Real field: `status` (running|stopped|failed). `state` kept as fallback. */
  status?: string;
  state?: string;
  active_state?: string;
  sub_state?: string;
  load_state?: string;
  description?: string;
}

export function useServiceStatus(serviceId: string | undefined, id: string | undefined, node?: string) {
  return useQuery({
    queryKey: [...k8sKeys.cluster(serviceId ?? '', id ?? ''), 'service_status', node ?? ''] as const,
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () => {
      const body = unwrap<{ services?: ServiceStatusEntry[] } | ServiceStatusEntry[]>(
        await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/deployments/service_status`), { params: node ? { node } : undefined }),
      );
      return arr<ServiceStatusEntry>(body, 'services');
    },
  });
}

// ---- Snapshot schedule ---------------------------------------------------

export function useSnapshotSchedule(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: [...k8sKeys.snapshots(serviceId ?? '', id ?? ''), 'schedule'] as const,
    enabled: !!serviceId && !!id,
    retry: false,
    // Real fields: `cron` and `retention` (legacy SnapshotSchedule type).
    // `frequency`/`retain` kept as fallbacks for older gateway versions.
    queryFn: async () => unwrap<{ enabled?: boolean; cron?: string; retention?: number; frequency?: string; retain?: number }>(
      await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/snapshots/schedule`)),
    ),
  });
}

export function useSetSnapshotSchedule(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.put(k8sPath(serviceId, `clusters/${clusterId}/snapshots/schedule`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: [...k8sKeys.snapshots(serviceId, clusterId), 'schedule'] }); options?.onSuccess?.(...a); },
  });
}

export function useUpdateCluster(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.put(k8sPath(serviceId, `clusters/${clusterId}`), p)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: k8sKeys.cluster(serviceId, clusterId) });
      qc.invalidateQueries({ queryKey: k8sKeys.clusters(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Snapshots -----------------------------------------------------------

export interface ClusterSnapshot {
  id: string;
  name?: string;
  status?: string;
  created_at?: string | number;
}

export function useClusterSnapshots(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.snapshots(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => arr<ClusterSnapshot>(unwrap(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/snapshots`))), 'snapshots'),
  });
}

export function useCreateClusterSnapshot(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown> | void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p?: Record<string, unknown>) => (await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/snapshots`), p ?? {})).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.snapshots(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteClusterSnapshot(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sid: string) => (await serviceApi.delete(k8sPath(serviceId, `clusters/${clusterId}/snapshots/${sid}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.snapshots(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

export function useRestoreClusterSnapshot(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sid: string) => (await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/snapshots/${sid}/restore`), {})).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.cluster(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}
